import * as ko from 'knockout'
import {Context, Router} from '@profiscience/knockout-contrib-router';
import {computed, observable} from 'knockout-decorators'
import {MenuViewModelContext} from "../menu/menu";
import {postbox} from "../../components/util/postbox";
import {EmailDto, emarsysApi, myTripChecklistApi} from "../../api/api-wrapper";
import globalState from '../../global-state';
import i18nextko from "../../components/util/i18nko";
import {isSharedTrip} from "../share/shareUtils";

class ViewModelContext extends Context {
    $parent: MenuViewModelContext;
    fileName: string;
    pdf: Blob;
}

class Form {

    public email: KnockoutObservable<string> =
        ko.observable('').extend({required: true, email: true});

    public message: KnockoutObservable<string> =
        ko.observable('').extend({required: true, maxLength: 500});

    public confirm: KnockoutObservable<boolean> =
        ko.observable(false).extend({confirmed: true});
}

class ViewModel {

    public pageTitle: KnockoutObservable<string>;

    public form: Form;

    @observable({expose: true})
    public fileName: String = "";

    @observable({expose: true})
    public pdf: Blob;

    public tripId: number;

    public validationStatus: KnockoutObservable<any>;

    constructor(ctx: ViewModelContext) {
        this.pageTitle = ko.observable('send e-mail');
        this.fileName = ctx.fileName;
        this.pdf = ctx.pdf;
        this.tripId = globalState.currentTrip.id;
        this.form = new Form();
        this.form.message(this.defaultMessageText());

        this.validationStatus = ko.validatedObservable({
            email: this.form.email,
            message: this.form.message,
            confirm: this.form.confirm
        });

        this.sendForm = this.sendForm.bind(this);
        this.showPdf = this.showPdf.bind(this);
    }

    @computed
    get fileSize(): string {
        if (this.pdf.size / 1024 > 1000) {
            return (this.pdf.size / 1024 / 1024).toFixed(2) + " MB";

        } else {
            return (this.pdf.size / 1024).toFixed(2) + " KB";
        }
    }

    @computed
    get dataUrl(): string {
        return URL.createObjectURL(this.pdf);
    }

    public showPdf() {
        if (window.navigator && (window.navigator as any).msSaveOrOpenBlob) {
            const pdf = new Blob([this.pdf], {type: 'application/pdf'});
            (window.navigator as any).msSaveOrOpenBlob(pdf, `${this.fileName}.pdf`);
        }
        return true;
    }

    public sendForm() {
        const orderErrors = ko.validation.group(this.form);
        if (!this.validationStatus.isValid() || orderErrors().length > 0) {
            orderErrors.showAllMessages();
            postbox.addError('validation.failed');
            return;
        } else {
            globalState.loading(true);
            return emarsysApi
                .sendChecklist(this.tripId.toString(), <EmailDto>{
                    recipients: [
                        this.form.email()
                    ],
                    message: this.form.message(),
                    name: `${globalState.user().firstname} ${globalState.user().lastname}`
                })
                .then(() => {
                    globalState.loading(false);
                    postbox.clearErrors();
                    postbox.addSuccess('send.successes.emailSent');

                    // refresh page and validations
                    return Router.update(`/reise/${this.tripId}/versenden`, {push: false, force: true});
                })
                .catch(() => {
                    globalState.loading(false);
                    postbox.addError('send.errors.failedToSendEmail');

                    return false;
                });
        }
    }

    /**
     * Check if the trip is shared.
     */
    @computed
    public get isSharedTrip() {
        return isSharedTrip(globalState.currentTrip);
    }

    /**
     * Get the default message text.
     */
    private defaultMessageText() {
        return i18nextko.t("send.defaultMessageText",
            {name: `${globalState.user().firstname} ${globalState.user().lastname}`}
        )();
    }
}

export default <KnockoutLazyPageDefinition>{
    viewModel: ViewModel,
    template: require('./send.html'),
    componentName: "send",
    loader: (ctx: ViewModelContext) => {
        return myTripChecklistApi.createChecklistPdf(String(globalState.currentTrip.id)).then(pdfData => {
            ctx.fileName = globalState.currentTrip.title;
            ctx.pdf = new Blob([pdfData], {type: 'application/pdf'});
            return Promise.resolve();
        });
    }
};
